import type { XamanPayloadResponse } from '../types/signing';

export type { XamanPayloadResponse };

const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';

export interface MutationResult {
  // Which path the backend took to process the mutation.
  //   - 'user-signed': the authenticated user IS the NFT issuer and must sign the
  //     NFTokenModify transaction themselves via Xaman. `payload` is populated.
  //   - 'queued': routed through the launchpad minter (issuer has authorized our
  //     wallet); batch is now processing. `batchId` is populated.
  //   - undefined (older servers): behaves like 'queued'.
  mode?: 'user-signed' | 'queued';

  // Present when mode === 'user-signed'
  signingIntent?: { intentId: string; provider: string; txJson?: Record<string, unknown> };
  payload?: XamanPayloadResponse;
  metadataUri?: string;
  metadataCid?: string;
  expectedMetadataVersion?: number;

  // Present when mode === 'queued'
  transactionHash?: string;
  newMetadataUri?: string;
  metadataVersion?: number;
  batchId?: string;
  status?: string;
  message?: string;
}

export interface MinterAuthorizationResult {
  authorized: boolean;
  currentMinter: string | null;
  requiredMinter: string;
}

export type AlternateImageInput =
  | { kind: 'keep'; uri: string; mime_type?: string }
  | { kind: 'new'; file: File; mime_type?: string };

/**
 * Mutate a single NFT (multipart upload).
 *
 * Pass `alternates` to rebuild `properties.files` alongside the primary image:
 *   - `{ kind: 'keep', uri }` preserves an existing alternate by IPFS URI.
 *   - `{ kind: 'new', file }` uploads a new image and pins it to IPFS server-side.
 * Omit `alternates` (or pass `undefined`) to leave existing properties.files alone.
 * Pass `[]` to clear all alternates.
 */
export async function mutateNFT(
  tokenId: string,
  data: {
    name: string;
    description?: string;
    traits?: Array<{ trait_type: string; value: string }>;
    image?: File;
    alternates?: AlternateImageInput[];
  }
): Promise<{ success: boolean; data?: MutationResult; error?: string }> {
  const formData = new FormData();
  formData.append('name', data.name);
  if (data.description !== undefined) {
    formData.append('description', data.description);
  }
  if (data.traits) {
    formData.append('traits', JSON.stringify(data.traits));
  }
  if (data.image) {
    formData.append('image', data.image);
  }

  if (data.alternates !== undefined) {
    const operations: Array<
      | { type: 'keep'; uri: string; mime_type?: string }
      | { type: 'new'; upload_index: number; mime_type?: string }
    > = [];
    let uploadIndex = 0;
    for (const alt of data.alternates) {
      if (alt.kind === 'keep') {
        operations.push({ type: 'keep', uri: alt.uri, ...(alt.mime_type ? { mime_type: alt.mime_type } : {}) });
      } else {
        formData.append('alternate_image', alt.file);
        operations.push({
          type: 'new',
          upload_index: uploadIndex,
          ...(alt.mime_type ? { mime_type: alt.mime_type } : { mime_type: alt.file.type || undefined }),
        });
        uploadIndex++;
      }
    }
    formData.append('alternate_operations', JSON.stringify(operations));
  }

  const token = localStorage.getItem('xrpl_marketplace_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}/nfts/${tokenId}/mutate`, {
    method: 'POST',
    body: formData,
    headers,
    credentials: 'include',
  });

  const result = await response.json();

  if (!response.ok) {
    // Check if it's an authorization error with data
    if (result.data?.requiresAuthorization) {
      return {
        success: false,
        error: 'NFTokenMinter authorization required',
        data: result.data,
      };
    }
    throw new Error(result.error || result.message || 'Failed to mutate NFT');
  }

  return result;
}

/**
 * Create a Xaman payload for the issuer to authorize the minter wallet
 */
export async function requestMinterAuthorization(
  tokenId: string
): Promise<{ success: boolean; data?: { signingIntent?: { intentId: string; provider: string; txJson?: Record<string, unknown> }; payload?: XamanPayloadResponse; minterAddress: string }; error?: string }> {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}/nfts/${tokenId}/authorize-minter`, {
    method: 'POST',
    headers,
    credentials: 'include',
  });

  return response.json();
}

/**
 * Check if the issuer has authorized our minter wallet
 */
export async function checkMinterAuthorization(
  tokenId: string
): Promise<{ success: boolean; data?: MinterAuthorizationResult; error?: string }> {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}/nfts/${tokenId}/minter-authorization`, {
    headers,
    credentials: 'include',
  });

  return response.json();
}

// ============================================================================
// Bulk Mutation Batch API
// ============================================================================

const mutationsBase = (projectSlug: string | null, collectionSlug: string) =>
  projectSlug
    ? `${API_BASE_URL}/creator/projects/${projectSlug}/collections/${collectionSlug}/manage/mutations`
    : `${API_BASE_URL}/creator/collections/${collectionSlug}/manage/mutations`;

export interface MutationBatch {
  id: string;
  name: string | null;
  status: string;
  total_count: number;
  completed_count: number;
  failed_count: number;
  scheduled_at: string | null;
  show_countdown: boolean;
  countdown_message: string | null;
  auto_pause_reason: string | null;
  payment_amount_drops: string | null;
  payment_tx_hash: string | null;
  payment_confirmed_at: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface MutationFeePayload {
  signingIntent?: { intentId: string; provider: string; txJson?: Record<string, unknown> };
  payload?: XamanPayloadResponse;
  fee: {
    totalItems: number;
    feeDrops: number;
    feeXrp: number;
    perNftFeeXrp: number;
    freeTier: number;
    billableCount: number;
  };
}

export interface MutationFeeStatus {
  status: 'pending' | 'paid' | 'failed' | 'rejected' | 'expired' | 'unknown';
  txHash?: string;
  batchStatus?: string;
  message?: string;
}

export interface BatchCreateResult {
  batchId: string;
  status: string;
  totalCount: number;
  imageReferences: number;
  needsImages: boolean;
  validationErrors: string[];
  validationWarnings: string[];
}

export interface PreviewItem {
  itemId: string;
  nftTokenId: string;
  status: string;
  currentName: string;
  currentDescription: string | null;
  currentImageUrl: string | null;
  currentTraits: Array<{ trait_type: string; value: string }>;
  newName: string;
  newDescription: string | null;
  newImageFilename: string | null;
  newTraits: Array<{ trait_type: string; value: string }>;
  changes: string[];
  traitDiff: {
    added: string[];
    removed: string[];
    changed: Array<{ trait: string; from: string; to: string }>;
  };
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function handleResponse<T>(response: Response): Promise<{ success: boolean; data?: T; error?: string }> {
  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.error || result.message || 'Request failed');
  }
  return result;
}

/**
 * Download mutation CSV for a collection's mutable NFTs
 */
export async function downloadMutationCSV(
  projectSlug: string | null,
  collectionSlug: string
): Promise<Blob> {
  const response = await fetch(`${mutationsBase(projectSlug, collectionSlug)}/csv`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const result = await response.json().catch(() => ({ error: 'Download failed' }));
    throw new Error(result.error || 'Failed to download CSV');
  }

  return response.blob();
}

/**
 * List mutation batches for a collection
 */
export async function listMutationBatches(
  projectSlug: string | null,
  collectionSlug: string,
  options?: { status?: string; page?: number; limit?: number }
): Promise<{ batches: MutationBatch[]; pagination: { page: number; limit: number; total: number } }> {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  if (options?.page) params.set('page', String(options.page));
  if (options?.limit) params.set('limit', String(options.limit));

  const qs = params.toString();
  const url = `${mutationsBase(projectSlug, collectionSlug)}/batches${qs ? `?${qs}` : ''}`;

  const response = await fetch(url, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  const result = await handleResponse<{ batches: MutationBatch[]; pagination: any }>(response);
  return result.data!;
}

/**
 * Get a single batch with status details
 */
export async function getMutationBatch(
  projectSlug: string | null,
  collectionSlug: string,
  batchId: string
): Promise<MutationBatch & { statusCounts: Record<string, number>; recentItems: any[]; estimatedCompletionAt: string | null }> {
  const response = await fetch(`${mutationsBase(projectSlug, collectionSlug)}/batches/${batchId}`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  const result = await handleResponse<any>(response);
  return result.data!;
}

/**
 * Create a new mutation batch by uploading a CSV
 */
export async function createMutationBatch(
  projectSlug: string | null,
  collectionSlug: string,
  csvFile: File,
  options?: { name?: string; description?: string }
): Promise<BatchCreateResult> {
  const formData = new FormData();
  formData.append('csv', csvFile);
  if (options?.name) formData.append('name', options.name);
  if (options?.description) formData.append('description', options.description);

  const response = await fetch(`${mutationsBase(projectSlug, collectionSlug)}/batches`, {
    method: 'POST',
    body: formData,
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  const result = await handleResponse<BatchCreateResult>(response);
  return result.data!;
}

export interface PresignedImageUrl {
  filename: string;
  uploadUrl: string;
  cdnUrl: string;
  s3Key: string;
}

/**
 * Get presigned S3 URLs for direct browser-to-S3 image uploads
 */
export async function getPresignedMutationImageUrls(
  projectSlug: string | null,
  collectionSlug: string,
  batchId: string,
  items: { filename: string; contentType: string }[]
): Promise<PresignedImageUrl[]> {
  const response = await fetch(
    `${mutationsBase(projectSlug, collectionSlug)}/batches/${batchId}/presign-images`,
    {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
      credentials: 'include',
    }
  );

  const result = await handleResponse<{ urls: PresignedImageUrl[] }>(response);
  return result.data!.urls;
}

/**
 * Upload a single file directly to S3 using a presigned PUT URL
 */
export async function uploadFileToS3(
  presignedUrl: string,
  file: File,
  contentType: string
): Promise<void> {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });

  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status}`);
  }
}

export interface ConfirmImagesResult {
  confirmed: number;
  missingCount: number;
  allImagesUploaded: boolean;
  status: string;
}

/**
 * Confirm that images have been uploaded to S3 and update batch items
 */
export async function confirmBatchImages(
  projectSlug: string | null,
  collectionSlug: string,
  batchId: string,
  uploadedFiles: { filename: string; s3Key: string }[]
): Promise<ConfirmImagesResult> {
  const response = await fetch(
    `${mutationsBase(projectSlug, collectionSlug)}/batches/${batchId}/confirm-images`,
    {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uploadedFiles }),
      credentials: 'include',
    }
  );

  const result = await handleResponse<ConfirmImagesResult>(response);
  return result.data!;
}

/**
 * Get preview/diff for a batch
 */
export async function getBatchPreview(
  projectSlug: string | null,
  collectionSlug: string,
  batchId: string,
  options?: { page?: number; limit?: number }
): Promise<{
  batchId: string;
  status: string;
  ready: boolean;
  totalCount: number;
  items: PreviewItem[];
  pagination: { page: number; limit: number; total: number };
}> {
  const params = new URLSearchParams();
  if (options?.page) params.set('page', String(options.page));
  if (options?.limit) params.set('limit', String(options.limit));

  const qs = params.toString();
  const url = `${mutationsBase(projectSlug, collectionSlug)}/batches/${batchId}/preview${qs ? `?${qs}` : ''}`;

  const response = await fetch(url, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  const result = await handleResponse<any>(response);
  return result.data!;
}

/**
 * Remove an item from a batch
 */
export async function removeBatchItem(
  projectSlug: string | null,
  collectionSlug: string,
  batchId: string,
  itemId: string
): Promise<void> {
  const response = await fetch(
    `${mutationsBase(projectSlug, collectionSlug)}/batches/${batchId}/items/${itemId}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include',
    }
  );

  await handleResponse(response);
}

/**
 * Submit or schedule a batch for processing
 */
export async function submitMutationBatch(
  projectSlug: string | null,
  collectionSlug: string,
  batchId: string,
  options?: {
    scheduledAt?: string;
    showCountdown?: boolean;
    countdownMessage?: string;
  }
): Promise<{
  batchId: string;
  status: string;
  totalCount: number;
  scheduledAt: string | null;
  estimatedProcessingSeconds: number;
  paymentRequired?: boolean;
  fee?: {
    feeDrops: number;
    feeXrp: number;
    perNftFeeXrp: number;
    freeTier: number;
    billableCount: number;
  };
}> {
  const response = await fetch(
    `${mutationsBase(projectSlug, collectionSlug)}/batches/${batchId}/submit`,
    {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options || {}),
      credentials: 'include',
    }
  );

  const result = await handleResponse<any>(response);
  return result.data!;
}

/**
 * Pause a processing batch
 */
export async function pauseMutationBatch(
  projectSlug: string | null,
  collectionSlug: string,
  batchId: string
): Promise<void> {
  const response = await fetch(
    `${mutationsBase(projectSlug, collectionSlug)}/batches/${batchId}/pause`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
    }
  );

  await handleResponse(response);
}

/**
 * Cancel a batch
 */
export async function cancelMutationBatch(
  projectSlug: string | null,
  collectionSlug: string,
  batchId: string
): Promise<void> {
  const response = await fetch(
    `${mutationsBase(projectSlug, collectionSlug)}/batches/${batchId}/cancel`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
    }
  );

  await handleResponse(response);
}

/**
 * Resume a paused batch
 */
export async function resumeMutationBatch(
  projectSlug: string | null,
  collectionSlug: string,
  batchId: string
): Promise<void> {
  const response = await fetch(
    `${mutationsBase(projectSlug, collectionSlug)}/batches/${batchId}/resume`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
    }
  );

  await handleResponse(response);
}

/**
 * Create a fee payment payload for a mutation batch
 */
export async function createMutationFeePayment(
  projectSlug: string | null,
  collectionSlug: string,
  batchId: string
): Promise<MutationFeePayload> {
  const response = await fetch(
    `${mutationsBase(projectSlug, collectionSlug)}/batches/${batchId}/fee-payment`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
    }
  );

  const result = await handleResponse<any>(response);
  return result.data!;
}

/**
 * Check the status of a mutation batch fee payment
 */
export async function checkMutationFeePaymentStatus(
  projectSlug: string | null,
  collectionSlug: string,
  batchId: string,
  payloadUuid: string
): Promise<MutationFeeStatus> {
  const response = await fetch(
    `${mutationsBase(projectSlug, collectionSlug)}/batches/${batchId}/fee-payment/${payloadUuid}`,
    {
      headers: getAuthHeaders(),
      credentials: 'include',
    }
  );

  const result = await handleResponse<any>(response);
  return result.data!;
}

/**
 * Get active countdown for a collection (public endpoint)
 */
export async function getCollectionCountdown(
  slug: string
): Promise<{ active: boolean; message?: string; scheduledAt?: string; totalCount?: number }> {
  const response = await fetch(`${API_BASE_URL}/collections/${slug}/countdown`);
  const result = await response.json();
  return result.data;
}
